import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { colors, LoadingSpinner, Card, EmptyState, Button } from '../../shared';
import { ThumbsUp, ThumbsDown, MessageSquare, Filter, ChevronDown, ExternalLink } from 'lucide-react';

const FeedbackCard = ({ feedback }) => {
  const [expanded, setExpanded] = useState(false);

  const ratingColor = feedback.rating === 'up' ? colors.green : colors.red;
  const RatingIcon = feedback.rating === 'up' ? ThumbsUp : ThumbsDown;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card className="mb-3" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg" style={{ background: `${ratingColor}20` }}>
          <RatingIcon size={18} style={{ color: ratingColor }} />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-medium" style={{ color: colors.textPrimary }}>
                {feedback.promptId}
              </p>
              <p className="text-xs" style={{ color: colors.textMuted }}>
                v{feedback.promptVersion} | {feedback.product} | {formatDate(feedback.ratedAt)}
              </p>
            </div>
            <span
              className="px-2 py-0.5 rounded text-xs"
              style={{ background: colors.cardBg, color: colors.textSecondary }}
            >
              {feedback.raterRole}
            </span>
          </div>

          {feedback.comment && (
            <p className="text-sm mb-2" style={{ color: colors.textSecondary }}>
              "{feedback.comment}"
            </p>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs flex items-center gap-1"
            style={{ color: colors.textMuted }}
          >
            <ChevronDown
              size={14}
              className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
            {expanded ? 'Hide details' : 'Show input/output'}
          </button>

          {expanded && (
            <div className="mt-3 space-y-2">
              {feedback.inputSnapshot && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Input:</p>
                  <pre
                    className="text-xs p-2 rounded overflow-auto max-h-32"
                    style={{ background: colors.inputBg, color: colors.textSecondary }}
                  >
                    {JSON.stringify(feedback.inputSnapshot, null, 2)}
                  </pre>
                </div>
              )}
              {feedback.outputSnapshot && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Output:</p>
                  <pre
                    className="text-xs p-2 rounded overflow-auto max-h-32"
                    style={{ background: colors.inputBg, color: colors.textSecondary }}
                  >
                    {typeof feedback.outputSnapshot === 'string'
                      ? feedback.outputSnapshot
                      : JSON.stringify(feedback.outputSnapshot, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default function FeedbackTab({ permissions }) {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [filterRating, setFilterRating] = useState('all');
  const [filterProduct, setFilterProduct] = useState('all');

  useEffect(() => {
    loadFeedback();
  }, []);

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'prompt_feedback'));
      const feedbackData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const dateA = a.ratedAt?.toDate?.() || new Date(a.ratedAt || 0);
          const dateB = b.ratedAt?.toDate?.() || new Date(b.ratedAt || 0);
          return dateB - dateA;
        });
      setFeedback(feedbackData);
    } catch (error) {
      console.error('Error loading feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFeedback = feedback.filter(f => {
    const matchesRating = filterRating === 'all' || f.rating === filterRating;
    const matchesProduct = filterProduct === 'all' || f.product === filterProduct;
    return matchesRating && matchesProduct;
  });

  // Calculate stats
  const totalFeedback = feedback.length;
  const positiveCount = feedback.filter(f => f.rating === 'up').length;
  const negativeCount = feedback.filter(f => f.rating === 'down').length;
  const positiveRate = totalFeedback > 0 ? (positiveCount / totalFeedback) * 100 : 0;

  if (!permissions?.viewFeedback) {
    return (
      <div className="p-6">
        <EmptyState
          icon={MessageSquare}
          title="Access Denied"
          description="You don't have permission to view feedback"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>Feedback</h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            User feedback on AI responses
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <p className="text-sm" style={{ color: colors.textSecondary }}>Total Feedback</p>
          <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{totalFeedback}</p>
        </Card>
        <Card>
          <p className="text-sm" style={{ color: colors.textSecondary }}>Positive Rate</p>
          <p className="text-2xl font-bold" style={{ color: colors.green }}>{positiveRate.toFixed(0)}%</p>
        </Card>
        <Card>
          <p className="text-sm" style={{ color: colors.textSecondary }}>Negative Feedback</p>
          <p className="text-2xl font-bold" style={{ color: colors.red }}>{negativeCount}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
          className="px-4 py-2 rounded-xl outline-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        >
          <option value="all">All Ratings</option>
          <option value="up">Positive Only</option>
          <option value="down">Negative Only</option>
        </select>
        <select
          value={filterProduct}
          onChange={(e) => setFilterProduct(e.target.value)}
          className="px-4 py-2 rounded-xl outline-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        >
          <option value="all">All Products</option>
          <option value="trades-app">Trades App</option>
          <option value="insurance-estimator">Insurance Estimator</option>
        </select>
      </div>

      {/* Feedback List */}
      {filteredFeedback.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No feedback yet"
          description="Feedback will appear here when users rate AI responses"
        />
      ) : (
        <div>
          {filteredFeedback.map(fb => (
            <FeedbackCard key={fb.id} feedback={fb} />
          ))}
        </div>
      )}
    </div>
  );
}
